import { useState } from "react";
import { Check, CheckCircle2, ExternalLink, Loader2, MapPinned, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PinMap } from "@/components/delivery/mapParts";
import { api, digits, errMsg } from "@/lib/api";
import { addrMissing, mapsLink } from "@/lib/boy";

const spin = <Loader2 className="mr-2 h-4 w-4 animate-spin" />;

function Saved({ value, onAdjust, testid }) {
  return (
    <div className="space-y-3 rounded-xl border-2 border-emerald-500/50 bg-emerald-500/5 p-3" data-testid={`${testid}-saved`}>
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
        <div className="min-w-0">
          <p className="text-sm font-semibold">Localização salva</p>
          <p className="text-xs text-muted-foreground">O entregador recebe este ponto quando aceitar a corrida.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 pl-8">
        <Button type="button" size="sm" variant="outline" onClick={onAdjust} data-testid={`${testid}-adjust-btn`}><MapPinned className="mr-1.5 h-4 w-4" />Ajustar no mapa</Button>
        <a href={mapsLink(`${value[0]},${value[1]}`)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`${testid}-view-link`}>Ver no mapa<ExternalLink className="h-3 w-3" /></a>
      </div>
    </div>
  );
}

export function MapCheck({ code, addr, value, onSave, testid = "boy-merchant-map" }) {
  const [draft, setDraft] = useState(null);
  const [focus, setFocus] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const locate = async () => {
    const m = addrMissing(addr, "empresa");
    if (m) return toast.error(m);
    setBusy("find");
    setNote("");
    try {
      const { data } = await api.post(`/public/boy/${code}/geocode`, { ...addr, cep: digits(addr.cep) });
      const p = [data.lat, data.lng];
      setFocus(p);
      setDraft(p);
      if (data.precision !== "endereco") setNote("Localização aproximada. Arraste o mapa até o ponto vermelho ficar na porta da sua loja.");
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy("");
    }
  };
  const save = async () => {
    setBusy("save");
    try {
      await onSave(draft);
      setDraft(null);
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy("");
    }
  };
  const adjust = () => {
    setNote("");
    setFocus(value);
    setDraft(value);
  };
  if (!draft) {
    return value ? <Saved value={value} onAdjust={adjust} testid={testid} /> : (
      <Button type="button" variant="outline" className="h-11 w-full" onClick={locate} disabled={!!busy} data-testid={`${testid}-btn`}>
        {busy ? spin : <MapPinned className="mr-2 h-4 w-4" />}Conferir no mapa
      </Button>
    );
  }
  return (
    <div className="space-y-3" data-testid={`${testid}-editor`}>
      {note && <p className="text-xs text-amber-600 dark:text-amber-400" data-testid={`${testid}-note`}>{note}</p>}
      <PinMap point={draft} focus={focus} onPick={setDraft} />
      <p className="text-xs text-muted-foreground" data-testid={`${testid}-hint`}>Arraste o mapa até o ponto vermelho ficar na porta da sua loja e toque em <b>Salvar localização</b>.</p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" className="h-11 flex-1" onClick={save} disabled={!!busy} data-testid={`${testid}-save-btn`}>
          {busy === "save" ? spin : <Check className="mr-2 h-4 w-4" />}Salvar localização
        </Button>
        <Button type="button" variant="outline" className="h-11" onClick={locate} disabled={!!busy} data-testid={`${testid}-refind-btn`}>
          {busy === "find" ? spin : <Search className="mr-2 h-4 w-4" />}Buscar pelo endereço
        </Button>
        <Button type="button" variant="ghost" className="h-11" onClick={() => setDraft(null)} disabled={!!busy} data-testid={`${testid}-cancel-btn`}>Cancelar</Button>
      </div>
    </div>
  );
}
